import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../../core/api/client';
import { FaSearch, FaFilter, FaUser, FaClock, FaInfoCircle } from 'react-icons/fa';
import './ActivityLogs.css';

function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterActionType, setFilterActionType] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, [currentPage, filterUserId, filterActionType, filterEntityType]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 50
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filterUserId) params.append('user_id', filterUserId);
      if (filterActionType) params.append('action_type', filterActionType);
      if (filterEntityType) params.append('entity_type', filterEntityType);

      const response = await api.get(`/activity-logs?${params}`);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setTotalLogs(response.data.total);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLogs();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionTypeLabel = (actionType) => {
    const labels = {
      'login': 'Prihlásenie',
      'login_failed': 'Neúspešné prihlásenie',
      'create_order': 'Vytvorenie objednávky',
      'update_order': 'Úprava objednávky',
      'delete_order': 'Zmazanie objednávky',
      'create_reminder': 'Vytvorenie pripomienky',
      'update_reminder': 'Úprava pripomienky',
      'delete_reminder': 'Zmazanie pripomienky',
      'create_note': 'Vytvorenie poznámky',
      'update_note': 'Úprava poznámky',
      'delete_note': 'Zmazanie poznámky',
      'create_sale': 'Vytvorenie predaja',
      'update_sale': 'Úprava predaja',
      'delete_sale': 'Zmazanie predaja',
      'cron_started': 'CRON spustený',
      'cron_completed': 'CRON dokončený',
      'cron_error': 'CRON chyba',
      'sashe_processing_started': 'Sashe spracovanie začaté',
      'sashe_processing_completed': 'Sashe spracovanie dokončené',
      'sashe_processing_failed': 'Sashe spracovanie zlyhalo',
      'sashe_processing_error': 'Sashe chyba spracovania'
    };
    return labels[actionType] || actionType;
  };

  const getActionTypeColor = (actionType) => {
    if (actionType.includes('create')) return '#10b981';
    if (actionType.includes('update')) return '#3b82f6';
    if (actionType.includes('delete')) return '#ef4444';
    if (actionType === 'login') return '#8b5cf6';
    if (actionType === 'login_failed') return '#f59e0b';
    if (actionType.includes('cron')) return '#06b6d4';
    if (actionType.includes('sashe')) return '#ec4899';
    if (actionType.includes('error') || actionType.includes('failed')) return '#ef4444';
    if (actionType.includes('completed')) return '#10b981';
    return '#6b7280';
  };

  return (
    <div className="activity-logs-container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1>Aktivitné logy</h1>

        <div className="filters-container">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Hľadať v logoch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-button">
                Hľadať
              </button>
            </div>
          </form>

          <div className="filter-row">
            <div className="filter-group">
              <label>
                <FaUser className="filter-icon" />
                Používateľ:
              </label>
              <select
                value={filterUserId}
                onChange={(e) => {
                  setFilterUserId(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="">Všetci</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>
                <FaFilter className="filter-icon" />
                Typ akcie:
              </label>
              <select
                value={filterActionType}
                onChange={(e) => {
                  setFilterActionType(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="">Všetky</option>
                <option value="login">Prihlásenie</option>
                <option value="login_failed">Neúspešné prihlásenie</option>
                <option value="create_order">Vytvorenie objednávky</option>
                <option value="update_order">Úprava objednávky</option>
                <option value="delete_order">Zmazanie objednávky</option>
                <option value="create_reminder">Vytvorenie pripomienky</option>
                <option value="update_reminder">Úprava pripomienky</option>
                <option value="delete_reminder">Zmazanie pripomienky</option>
                <option value="cron_started">CRON spustený</option>
                <option value="cron_completed">CRON dokončený</option>
                <option value="sashe_processing_started">Sashe spracovanie</option>
                <option value="sashe_processing_completed">Sashe dokončené</option>
              </select>
            </div>

            <div className="filter-group">
              <label>
                <FaInfoCircle className="filter-icon" />
                Typ entity:
              </label>
              <select
                value={filterEntityType}
                onChange={(e) => {
                  setFilterEntityType(e.target.value);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="">Všetky</option>
                <option value="order">Objednávka</option>
                <option value="reminder">Pripomienka</option>
                <option value="note">Poznámka</option>
                <option value="sale">Predaj</option>
              </select>
            </div>
          </div>
        </div>

        <div className="logs-info">
          <p>Celkový počet záznamov: <strong>{totalLogs}</strong></p>
        </div>

        {loading ? (
          <div className="loading">Načítavam logy...</div>
        ) : logs.length === 0 ? (
          <div className="no-logs">
            <p>Žiadne logy nenájdené</p>
          </div>
        ) : (
          <div className="logs-table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Čas</th>
                  <th>Používateľ</th>
                  <th>Akcia</th>
                  <th>Entita</th>
                  <th>Detaily</th>
                  <th>IP Adresa</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td>
                      <div className="timestamp">
                        <FaClock className="time-icon" />
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </td>
                    <td>
                      <div className="user-info">
                        <FaUser className="user-icon" />
                        {log.username || 'Neznámy'}
                      </div>
                    </td>
                    <td>
                      <span 
                        className="action-badge"
                        style={{ backgroundColor: getActionTypeColor(log.action_type) }}
                      >
                        {getActionTypeLabel(log.action_type)}
                      </span>
                    </td>
                    <td>
                      {log.entity_type && (
                        <span className="entity-type">
                          {log.entity_type}
                          {log.entity_id && ` #${log.entity_id}`}
                        </span>
                      )}
                    </td>
                    <td>
                      {log.details && (
                        <div className="details">
                          {typeof log.details === 'object' ? (
                            Object.entries(log.details).map(([key, value]) => (
                              <div key={key} className="detail-item">
                                <span className="detail-key">{key}:</span>
                                <span className="detail-value">
                                  {typeof value === 'object' 
                                    ? JSON.stringify(value, null, 2)
                                    : String(value)
                                  }
                                </span>
                              </div>
                            ))
                          ) : (
                            <span>{log.details}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="ip-address">{log.ip_address || '-'}</span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Predchádzajúca
            </button>
            <span className="page-info">
              Strana {currentPage} z {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Ďalšia
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default ActivityLogs;